// src/PassportScanner.tsx
import React, { useState } from 'react';

interface PassportScannerProps {
  onAutoFill: (data: {
    surname?: string;
    givenName?: string;
    passportNumber?: string;
    dobApplicant?: string;
    passportDateExpiry?: string;
    gender?: string;
  }) => void;
}

export const PassportScanner: React.FC<PassportScannerProps> = ({ onAutoFill }) => {
  const [showModal, setShowModal] = useState(false);
  const [line1Input, setLine1Input] = useState('');
  const [line2Input, setLine2Input] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanStatus, setScanStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const parseMRZText = (text: string) => {
    // Clean text and look for MRZ patterns
    const cleanText = text.toUpperCase().replace(/\r/g, '');
    const lines = cleanText
      .split('\n')
      .map((l) => l.replace(/[^A-Z0-9<]/g, '').trim())
      .filter((l) => l.length >= 28);

    // Look for line starting with P< or containing <<
    let line1 = '';
    let line2 = '';

    for (let i = 0; i < lines.length; i++) {
      if (lines[i].startsWith('P<') || (lines[i].includes('<<') && lines[i].length >= 30)) {
        line1 = lines[i];
        if (i + 1 < lines.length) {
          line2 = lines[i + 1];
        }
        break;
      }
    }

    if (!line1 && lines.length >= 2) {
      line1 = lines[lines.length - 2];
      line2 = lines[lines.length - 1];
    }

    if (line1 && line2) {
      setLine1Input(line1);
      setLine2Input(line2);
      parseMRZLines(line1, line2);
    } else {
      throw new Error('Could not auto-detect MRZ lines in image. Please ensure MRZ lines are clear.');
    }
  };

  const parseMRZLines = (line1Raw: string, line2Raw: string) => {
    try {
      const line1 = line1Raw.trim().toUpperCase();
      const line2 = line2Raw.trim().toUpperCase();

      if (!line1 || !line2) {
        throw new Error('Please enter both MRZ Line 1 and Line 2.');
      }

      // Line 1: P<LKASURNAME<<GIVEN<NAME<<<<<<<<<<<<<<<<<<<
      let surname = '';
      let givenName = '';

      const namePart = line1.length >= 5 ? line1.substring(5) : line1;
      const namePieces = namePart.split('<<');
      if (namePieces.length >= 1) {
        surname = namePieces[0].replace(/</g, ' ').trim();
      }
      if (namePieces.length >= 2) {
        givenName = namePieces[1].replace(/</g, ' ').trim();
      }

      // Line 2: N123456780LKA9505124M3005128<<<<<<<<<<<<<<02
      const passportNumber = line2.substring(0, 9).replace(/</g, '').trim();

      // DOB: YYMMDD
      let dobFormatted = '';
      if (line2.length >= 19) {
        const dobRaw = line2.substring(13, 19).replace(/O/g, '0').replace(/I/g, '1');
        if (/^\d{6}$/.test(dobRaw)) {
          const yy = parseInt(dobRaw.substring(0, 2), 10);
          const mm = dobRaw.substring(2, 4);
          const dd = dobRaw.substring(4, 6);
          const currentYY = new Date().getFullYear() % 100;
          const fullYear = yy > currentYY ? 1900 + yy : 2000 + yy;
          dobFormatted = `${fullYear}-${mm}-${dd}`;
        }
      }

      // Gender: M/F
      let gender = 'MALE';
      if (line2.length >= 21) {
        const gChar = line2.charAt(20);
        gender = gChar === 'F' ? 'FEMALE' : 'MALE';
      }

      // Expiry: YYMMDD
      let expFormatted = '';
      if (line2.length >= 27) {
        const expRaw = line2.substring(21, 27).replace(/O/g, '0').replace(/I/g, '1');
        if (/^\d{6}$/.test(expRaw)) {
          const yy = parseInt(expRaw.substring(0, 2), 10);
          const mm = expRaw.substring(2, 4);
          const dd = expRaw.substring(4, 6);
          const fullYear = 2000 + yy;
          expFormatted = `${fullYear}-${mm}-${dd}`;
        }
      }

      onAutoFill({
        surname,
        givenName,
        passportNumber,
        dobApplicant: dobFormatted,
        passportDateExpiry: expFormatted,
        gender,
      });

      setScanStatus({
        type: 'success',
        msg: `✅ Passport OCR Parsed! Auto-filled: ${surname} ${givenName} | Passport: ${passportNumber}`,
      });
      setShowModal(false);
    } catch (err: any) {
      setScanStatus({
        type: 'error',
        msg: `❌ ${err.message || 'Could not parse MRZ code.'}`,
      });
    }
  };

  const handleImageOCR = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsScanning(true);
    setScanProgress(10);
    setScanStatus(null);

    try {
      // Dynamically load Tesseract.js CDN if not already loaded
      if (!(window as any).Tesseract) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js';
          script.onload = resolve;
          script.onerror = reject;
          document.body.appendChild(script);
        });
      }

      setScanProgress(30);
      const Tesseract = (window as any).Tesseract;
      const worker = await Tesseract.createWorker('eng');

      setScanProgress(60);
      const ret = await worker.recognize(file);
      setScanProgress(90);

      await worker.terminate();
      setIsScanning(false);

      if (ret && ret.data && ret.data.text) {
        parseMRZText(ret.data.text);
      } else {
        throw new Error('No text recognized from passport image.');
      }
    } catch (error: any) {
      setIsScanning(false);
      setShowModal(true);
      setScanStatus({
        type: 'error',
        msg: `⚠️ OCR Reading: Please check/enter MRZ lines below if image text was unclear.`,
      });
    }
  };

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
        border: '2px dashed #2563eb',
        borderRadius: '12px',
        padding: '1.25rem',
        marginBottom: '1.5rem',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1.25rem' }}>🛂</span>
          <div>
            <h3 style={{ fontSize: '1.05rem', fontWeight: '800', color: '#1e3a8a', margin: 0 }}>
              Passport MRZ AI OCR Auto-Scanner
            </h3>
            <div style={{ fontSize: '0.8rem', color: '#3b82f6', fontWeight: '600' }}>
              Real-time Image Text Recognition & MRZ Parsing
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <label
            style={{
              background: '#2563eb',
              color: '#ffffff',
              padding: '0.55rem 1.1rem',
              borderRadius: '8px',
              fontSize: '0.85rem',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
            }}
          >
            📷 Upload Passport Photo (OCR)
            <input type="file" accept="image/*" onChange={handleImageOCR} style={{ display: 'none' }} />
          </label>

          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="btn-secondary"
            style={{ padding: '0.55rem 1rem', fontSize: '0.85rem' }}
          >
            ⌨️ Enter MRZ Manually
          </button>
        </div>
      </div>

      {isScanning && (
        <div style={{ marginTop: '0.75rem', background: '#ffffff', padding: '0.75rem', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
          <div style={{ fontSize: '0.85rem', color: '#1d4ed8', fontWeight: '700', marginBottom: '0.35rem' }}>
            ⏳ AI OCR Scanning Passport Image... ({scanProgress}%)
          </div>
          <div style={{ width: '100%', height: '6px', background: '#dbeafe', borderRadius: '9999px', overflow: 'hidden' }}>
            <div style={{ width: `${scanProgress}%`, height: '100%', background: '#2563eb', transition: 'width 0.3s ease' }} />
          </div>
        </div>
      )}

      {scanStatus && (
        <div
          style={{
            marginTop: '0.75rem',
            padding: '0.65rem 0.85rem',
            borderRadius: '8px',
            fontSize: '0.85rem',
            fontWeight: '700',
            background: scanStatus.type === 'success' ? '#f0fdf4' : '#fef2f2',
            border: scanStatus.type === 'success' ? '1px solid #86efac' : '1px solid #fca5a5',
            color: scanStatus.type === 'success' ? '#15803d' : '#dc2626',
          }}
        >
          {scanStatus.msg}
        </div>
      )}

      {/* MODAL FOR MANUAL ADJUSTMENT */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1rem',
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '520px',
              width: '100%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
            }}
          >
            <h3 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#0f172a', marginBottom: '0.35rem' }}>
              🛂 Passport MRZ Auto-Scanner
            </h3>
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1.25rem' }}>
              Look at the bottom 2 lines printed on your passport bio page (starting with <strong>P&lt;...</strong> and <strong>N...</strong>):
            </p>

            {/* VISUAL DIAGRAM */}
            <div
              style={{
                background: '#0f172a',
                color: '#38bdf8',
                fontFamily: 'monospace',
                padding: '0.85rem 1rem',
                borderRadius: '8px',
                fontSize: '0.8rem',
                marginBottom: '1.25rem',
                border: '1px solid #1e293b',
              }}
            >
              <div>Line 1: P&lt;LKASURNAME&lt;&lt;GIVEN&lt;NAME&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;</div>
              <div>Line 2: N123456780LKA9505124M3005128&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;02</div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', color: '#1e293b', marginBottom: '0.35rem' }}>
                MRZ Line 1 (Name Line):
              </label>
              <input
                type="text"
                placeholder="e.g. P<LKASURNAME<<GIVEN<NAME..."
                value={line1Input}
                onChange={(e) => setLine1Input(e.target.value.toUpperCase())}
                style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontFamily: 'monospace' }}
              />
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '700', color: '#1e293b', marginBottom: '0.35rem' }}>
                MRZ Line 2 (Data Line):
              </label>
              <input
                type="text"
                placeholder="e.g. N123456780LKA9505124M3005128..."
                value={line2Input}
                onChange={(e) => setLine2Input(e.target.value.toUpperCase())}
                style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontFamily: 'monospace' }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
              <label
                style={{
                  background: '#eff6ff',
                  color: '#2563eb',
                  padding: '0.5rem 0.85rem',
                  borderRadius: '8px',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  border: '1px solid #bfdbfe',
                }}
              >
                📷 Re-upload Image
                <input type="file" accept="image/*" onChange={handleImageOCR} style={{ display: 'none' }} />
              </label>

              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                  style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => parseMRZLines(line1Input, line2Input)}
                  className="btn-primary"
                  style={{ padding: '0.5rem 1.25rem', fontSize: '0.85rem' }}
                >
                  Parse & Auto-Fill ⚡
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
